import React, { useRef, useState, useEffect } from "react";
import { ShoppingCart, ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { listPublicVideoSpecials } from "@/lib/cms/video-specials.functions";
import { useAlacarteCart } from "@/hooks/useAlacarteCart";
import { cn } from "@/lib/utils";

export function TodaysSpecialsVideos() {
  const listFn = useServerFn(listPublicVideoSpecials);
  const { data: specials, isLoading } = useQuery({
    queryKey: ["video-specials", "public"],
    queryFn: () => listFn(),
    staleTime: 5 * 60 * 1000,
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener("scroll", checkScroll);
      checkScroll();
      window.addEventListener("resize", checkScroll);
      return () => {
        el.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      };
    }
  }, [specials]);

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    
    // Desktop: 6 cards visible
    const containerWidth = scrollRef.current.clientWidth;
    const scrollAmount = (containerWidth / 6) * 3; // Move 3 cards

    const newScrollLeft = dir === "left" 
      ? Math.max(0, scrollRef.current.scrollLeft - scrollAmount)
      : Math.min(scrollRef.current.scrollWidth - scrollRef.current.clientWidth, scrollRef.current.scrollLeft + scrollAmount);

    scrollRef.current.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });
  };

  if (isLoading) {
    return (
      <section className="py-12 bg-white overflow-hidden">
        <div className="container px-4 mx-auto">
          <div className="h-8 w-64 bg-muted animate-pulse rounded mb-4" />
          <div className="h-4 w-96 bg-muted/60 animate-pulse rounded mb-8" />
          <div className="flex gap-6 overflow-hidden">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="min-w-[280px] h-[500px] bg-muted animate-pulse rounded-2xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!specials || specials.length === 0) return null;

  return (
    <section className="py-16 bg-white overflow-hidden border-b border-muted/20">
      <div className="container px-4 mx-auto relative">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div className="space-y-2 text-left">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              TODAY'S SPECIALS
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Catch our freshest daily creations before they're gone.
            </p>
          </div>
        </div>

        {/* Desktop Centered Arrows */}
        <div className="hidden md:block">
          <Button
            variant="outline"
            size="icon"
            className={cn(
              "absolute left-0 top-[60%] -translate-y-1/2 -translate-x-1/2 z-30 bg-white rounded-full shadow-lg border-primary/20 hover:bg-primary hover:text-white transition-all w-12 h-12",
              !canScrollLeft && "hidden"
            )}
            onClick={() => scroll("left")}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className={cn(
              "absolute right-0 top-[60%] -translate-y-1/2 translate-x-1/2 z-30 bg-white rounded-full shadow-lg border-primary/20 hover:bg-primary hover:text-white transition-all w-12 h-12",
              !canScrollRight && "hidden"
            )}
            onClick={() => scroll("right")}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </div>

        <div 
          ref={scrollRef}
          className="flex overflow-x-auto md:overflow-x-hidden overflow-y-hidden gap-4 md:gap-4 pb-8 no-scrollbar scroll-smooth snap-x snap-mandatory px-0"
        >
          {specials.map((special) => (
            <div 
              key={special.id} 
              className="w-[85vw] md:w-[calc(16.666%-13.4px)] flex-shrink-0 snap-start flex"
            >
              <VideoCard special={special} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VideoCard({ special }: { special: any }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const { addItem: addToCart } = useAlacarteCart();

  // Any configured video URL is used (uploaded media URLs are extensionless,
  // e.g. /api/public/media/<id>). Falls back to the dish image on load error.
  const [videoFailed, setVideoFailed] = useState(false);
  const hasValidVideo =
    typeof special.video_url === "string" &&
    special.video_url.trim().length > 0 &&
    !special.video_url.includes("mixkit.co") &&
    !videoFailed;

  const handleAddToCart = async () => {
    if (!special.dish) return;
    if (!special.dish.is_active) {
      toast.error("This dish is currently unavailable");
      return;
    }

    try {
      await addToCart(special.dish.id, special.dish.meal_slot, 1);
      toast.success(`${special.dish.name} added to cart`);
    } catch (err: any) {
      if (err.message === "context_required") {
        toast.error("Please select a delivery area and date first");
      } else {
        toast.error("Failed to add to cart");
      }
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hasValidVideo) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [hasValidVideo]);

  const price = (special.dish?.alacarte_price_paise || 0) / 100;
  const displayImage = special.dish?.image_url || special.poster_url;

  return (
    <div 
      className="w-full bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-500 border border-muted/30 group flex flex-col h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative aspect-[9/16] overflow-hidden bg-muted flex-grow">
        {hasValidVideo ? (
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            autoPlay
            preload="auto"
            src={special.video_url}
            poster={special.poster_url || displayImage}
            onError={() => setVideoFailed(true)}
            className={cn(
              "w-full h-full object-cover transition-transform duration-700",
              isHovered ? "scale-105" : "scale-100"
            )}
          />

        ) : (
          <img 
            src={displayImage}
            alt={special.dish?.name}
            className={cn(
              "w-full h-full object-cover transition-transform duration-700",
              isHovered ? "scale-105" : "scale-100"
            )}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60" />
        <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
          {special.dish?.is_veg !== null && (
            <Badge className={special.dish?.is_veg ? "bg-green-600/90" : "bg-red-600/90"}>
              {special.dish?.is_veg ? "VEG" : "NON-VEG"}
            </Badge>
          )}
        </div>
      </div>

      <div className="p-2 md:p-3 flex flex-col gap-1 md:gap-2 flex-shrink-0">
        <div className="space-y-0.5">
          <h3 className="text-xs md:text-sm font-bold text-foreground leading-tight line-clamp-1">
            {special.dish?.name}
          </h3>
          <p className="hidden md:block text-[10px] text-muted-foreground line-clamp-2 h-6 leading-tight">
            {special.sales_description || special.dish?.description}
          </p>
        </div>
        <div className="flex items-center justify-between mt-auto">
          <div className="flex flex-col">
            <span className="text-sm md:text-lg font-black text-primary">₹{price}</span>
            {special.dish?.calories_kcal && (
              <span className="text-[9px] text-muted-foreground">
                {special.dish.calories_kcal} kcal
              </span>
            )}
          </div>
          <Button 
            size="sm"
            onClick={handleAddToCart}
            className="rounded-full px-2 md:px-4 font-bold hover:scale-105 transition-transform text-[9px] md:text-xs h-7 md:h-8"
          >
            <ShoppingCart className="h-3 w-3 md:h-3.5 md:w-3.5 mr-1" />
            ADD
          </Button>
        </div>
      </div>
    </div>
  );
}
